import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that manages subscription options dropdown.
 * Updates the displayed price when a subscription product is selected.
 *
 * @typedef {object} SubscriptionOptionsRefs
 *
 * @extends Component<SubscriptionOptionsRefs>
 */
class SubscriptionOptionsComponent extends Component {
  requiredRefs = [];

  /**
   * Gets the subscription select element.
   * Since it's nested inside subscription-dropdown-component, we find it manually.
   * @returns {HTMLSelectElement | null}
   */
  get subscriptionSelect() {
    return this.querySelector('select[name="subscription_option"]');
  }

  /**
   * Gets the hidden input element for the subscription variant ID.
   * Since it's nested inside subscription-dropdown-component, we find it manually.
   * @returns {HTMLInputElement | null}
   */
  get subscriptionVariantIdInput() {
    return this.querySelector('input[name="subscription_variant_id"]');
  }

  connectedCallback() {
    super.connectedCallback();

    const subscriptionSelect = this.subscriptionSelect;
    if (!subscriptionSelect) {
      console.warn('Subscription select not found');
      return;
    }

    subscriptionSelect.addEventListener('change', this.#handleSubscriptionChange);
    
    // Set up dialog trigger
    this.#setupDialogTrigger();
    
    // If there's a pre-selected option (first option), trigger price update on load
    const selectedValue = subscriptionSelect.value;
    if (selectedValue) {
      // Set the hidden input value
      const variantIdInput = this.subscriptionVariantIdInput;
      if (variantIdInput) {
        variantIdInput.value = selectedValue;
      }
      
      // Trigger price update for the pre-selected option
      const selectedOption = subscriptionSelect.options[subscriptionSelect.selectedIndex];
      if (selectedOption) {
        // Update right label with discount message from first product
        this.#updateRightLabel(selectedOption.dataset);
        
        const price = parseInt(selectedOption.dataset.price, 10);
        const originalPrice = selectedOption.dataset.originalPrice
          ? parseInt(selectedOption.dataset.originalPrice, 10)
          : null;
        const compareAtPrice = selectedOption.dataset.compareAtPrice
          ? parseInt(selectedOption.dataset.compareAtPrice, 10)
          : null;
        const discountPercentage = selectedOption.dataset.discountPercentage
          ? parseInt(selectedOption.dataset.discountPercentage, 10)
          : 0;
        
        // Update price display (price is already discounted if discount exists)
        this.#updatePriceDisplay({ 
          price, 
          originalPrice: discountPercentage > 0 ? originalPrice : null,
          compareAtPrice 
        });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const subscriptionSelect = this.subscriptionSelect;
    if (subscriptionSelect) {
      subscriptionSelect.removeEventListener('change', this.#handleSubscriptionChange);
    }
  }

  /**
   * Handles the subscription dropdown change event.
   * Updates the price display and stores the selected subscription variant ID.
   */
  #handleSubscriptionChange = async (event) => {
    const select = event.target;
    const subscriptionVariantId = select.value;
    
    // Get option data from either select or custom dropdown
    let optionData = null;
    const selectedOption = select.options[select.selectedIndex];
    
    if (selectedOption && selectedOption.dataset) {
      optionData = selectedOption.dataset;
    } else {
      // Try getting from custom dropdown
      const dropdownComponent = this.querySelector('subscription-dropdown-component');
      if (dropdownComponent) {
        const dropdownOption = dropdownComponent.refs.list?.querySelector(`[data-value="${subscriptionVariantId}"]`);
        if (dropdownOption) {
          optionData = dropdownOption.dataset;
        }
      }
    }

    // Update hidden input
    const variantIdInput = this.subscriptionVariantIdInput;
    if (variantIdInput) {
      variantIdInput.value = subscriptionVariantId;
    }

    // Update right label with discount message from selected product
    this.#updateRightLabel(optionData);

    if (!subscriptionVariantId || !optionData) {
      // No subscription selected, reset to main product price
      this.#updatePriceDisplay(null);
      return;
    }

    // Get price data from selected option (already discounted if discount exists)
    const price = parseInt(optionData.price, 10);
    const originalPrice = optionData.originalPrice
      ? parseInt(optionData.originalPrice, 10)
      : price;
    const compareAtPrice = optionData.compareAtPrice
      ? parseInt(optionData.compareAtPrice, 10)
      : null;
    const discountPercentage = optionData.discountPercentage
      ? parseInt(optionData.discountPercentage, 10)
      : 0;

    // Update price display (price is already the discounted price if discount exists)
    await this.#updatePriceDisplay({ 
      price, 
      originalPrice: discountPercentage > 0 ? originalPrice : null,
      compareAtPrice 
    });

    // Dispatch custom event for other components that might need to know about subscription selection
    this.dispatchEvent(
      new CustomEvent('subscription-selected', {
        bubbles: true,
        detail: {
          subscriptionVariantId,
          subscriptionProductId: optionData.productId,
          subscriptionProductTitle: optionData.productTitle,
          price,
          compareAtPrice,
        },
      })
    );
  };

  /**
   * Updates the price display across all product-price elements on the page.
   * @param {{price: number, originalPrice: number | null, compareAtPrice: number | null} | null} priceData - The price data to display, or null to reset.
   */
  async #updatePriceDisplay(priceData) {
    const productPriceElements = document.querySelectorAll('product-price');
    
    console.log('Updating price display, found', productPriceElements.length, 'price elements');

    for (const priceElement of productPriceElements) {
      if (!priceData) {
        // Reset to original price - fetch the current variant's price
        const productForm = this.closest('product-form-component');
        if (productForm) {
          const variantId = productForm.refs.variantId?.value;
          if (variantId) {
            // Fetch variant data and update
            await this.#fetchVariantAndUpdate(variantId, priceElement);
          }
        }
        return;
      }
      
      // Update price display using the option's price data
      this.#updatePriceFromOption(priceElement);
    }
  }

  /**
   * Formats and updates the price display using the selected option's price data.
   * @param {HTMLElement} priceElement - The price element to update.
   */
  #updatePriceFromOption(priceElement) {
    // Get selected option - try hidden select first, then custom dropdown
    let selectedOption = null;
    let optionData = null;
    
    // Try hidden select first
    const subscriptionSelect = this.subscriptionSelect;
    if (subscriptionSelect && subscriptionSelect.selectedIndex >= 0) {
      selectedOption = subscriptionSelect.options[subscriptionSelect.selectedIndex];
      if (selectedOption && selectedOption.value) {
        optionData = selectedOption.dataset;
      }
    }
    
    // If not found, try custom dropdown
    if (!optionData) {
      const dropdownComponent = this.querySelector('subscription-dropdown-component');
      if (dropdownComponent) {
        const selectedValue = dropdownComponent.getValue();
        const dropdownOption = dropdownComponent.refs.list?.querySelector(`[data-value="${selectedValue}"]`);
        if (dropdownOption) {
          optionData = dropdownOption.dataset;
        }
      }
    }
    
    if (!optionData || !optionData.value) {
      console.warn('No subscription option selected');
      return;
    }

    // Use pre-formatted prices from the option data attributes
    // Use discounted price if available, otherwise use original price
    const formattedPrice = optionData.formattedPrice || '';
    const formattedOriginalPrice = optionData.formattedOriginalPrice || '';
    const formattedComparePrice = optionData.formattedCompareAtPrice || '';
    const discountPercentage = optionData.discountPercentage
      ? parseInt(optionData.discountPercentage, 10)
      : 0;
    
    const price = parseInt(optionData.price, 10);
    const originalPrice = optionData.originalPrice
      ? parseInt(optionData.originalPrice, 10)
      : price;
    const compareAtPrice = optionData.compareAtPrice
      ? parseInt(optionData.compareAtPrice, 10)
      : null;
    
    if (!formattedPrice) {
      console.warn('No formatted price found in option data', optionData);
      return;
    }
    
    // Show compare price if there's a discount (show original) or if compare_at_price is higher
    let showComparePrice = false;
    let comparePriceToShow = null;
    
    if (discountPercentage > 0 && formattedOriginalPrice) {
      // Show original price as compare price when discount is applied
      showComparePrice = true;
      comparePriceToShow = formattedOriginalPrice;
    } else if (compareAtPrice && compareAtPrice > price && formattedComparePrice) {
      // Show compare_at_price if it's higher than the current price
      showComparePrice = true;
      comparePriceToShow = formattedComparePrice;
    }

    const priceHTML = this.#buildPriceHTML(formattedPrice, showComparePrice ? comparePriceToShow : null);
    
    // Find the price container - try multiple methods
    let currentPriceContainer = priceElement.querySelector('[ref="priceContainer"]');
    
    if (!currentPriceContainer) {
      // Try finding by attribute name
      currentPriceContainer = priceElement.querySelector('[ref*="price"]');
    }
    
    if (!currentPriceContainer) {
      // Fallback: look for a direct div child
      currentPriceContainer = priceElement.querySelector(':scope > div');
    }
    
    if (currentPriceContainer) {
      // Create a temporary container to parse the HTML
      const temp = document.createElement('div');
      temp.innerHTML = priceHTML.trim();
      
      // Replace the content
      currentPriceContainer.innerHTML = temp.innerHTML;
      console.log('Price updated to:', formattedPrice);
    } else {
      console.warn('Price container not found. Price element structure:', priceElement.innerHTML.substring(0, 200));
    }
  }

  /**
   * Fetches variant data and updates the price display.
   * @param {string} variantId - The variant ID to fetch.
   * @param {HTMLElement} priceElement - The price element to update.
   */
  async #fetchVariantAndUpdate(variantId, priceElement) {
    try {
      const response = await fetch(`/variants/${variantId}?view=price`);
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newPriceContainer = doc.querySelector('[ref="priceContainer"]');
        const currentPriceContainer = priceElement.querySelector('[ref="priceContainer"]');
        if (newPriceContainer && currentPriceContainer) {
          currentPriceContainer.replaceWith(newPriceContainer.cloneNode(true));
        }
      }
    } catch (error) {
      console.error('Failed to fetch variant price:', error);
    }
  }

  /**
   * Formats a price value using Shopify's money format.
   * @param {number} price - The price in cents.
   * @param {boolean} useCurrency - Whether to include currency code.
   * @returns {string} The formatted price string.
   */
  #formatPrice(price, useCurrency = false) {
    // Get money format from the page (Shopify injects this)
    const moneyFormatElement = document.querySelector('[data-money-format]');
    let moneyFormat = moneyFormatElement?.dataset.moneyFormat || '{{amount}}';
    
    // Format the amount
    const amount = (price / 100).toFixed(2);
    let formatted = moneyFormat.replace('{{amount}}', amount);
    
    // Add currency code if needed
    if (useCurrency && typeof Theme !== 'undefined' && Theme.currency) {
      formatted += ' ' + Theme.currency.iso_code;
    }
    
    return formatted;
  }

  /**
   * Builds the HTML for the price display.
   * @param {string} price - The formatted price.
   * @param {string | null} compareAtPrice - The formatted compare at price, if any.
   * @returns {string} The HTML string.
   */
  #buildPriceHTML(price, compareAtPrice) {
    if (compareAtPrice) {
      return `
        <span role="group">
          <span class="visually-hidden">Regular price</span>
          <span class="compare-at-price">${compareAtPrice}</span>
        </span>
        <span role="group">
          <span class="visually-hidden">Sale price</span>
          <span class="price">${price}</span>
        </span>
      `;
    }
    return `<span class="price">${price}</span>`;
  }

  /**
   * Gets the currently selected subscription variant ID.
   * @returns {string} The subscription variant ID, or empty string if none selected.
   */
  getSelectedSubscriptionVariantId() {
    const variantIdInput = this.subscriptionVariantIdInput;
    return variantIdInput ? variantIdInput.value : '';
  }

  /**
   * Gets the selling plan ID for the currently selected subscription option.
   * @returns {string} The selling plan ID, or empty string if none.
   */
  getSelectedSellingPlanId() {
    const subscriptionSelect = this.subscriptionSelect;
    if (!subscriptionSelect) return '';
    const selectedOption = subscriptionSelect.options[subscriptionSelect.selectedIndex];
    if (selectedOption && selectedOption.dataset.sellingPlanId) {
      return selectedOption.dataset.sellingPlanId;
    }
    return '';
  }

  /**
   * Updates the right label with the discount message from the selected product.
   * @param {DOMStringMap | null} optionData - The data attributes from the selected option.
   */
  #updateRightLabel(optionData) {
    const labelRight = this.querySelector('[ref="labelRight"]');
    if (!labelRight) return;

    if (optionData && optionData.discountMessage) {
      // Decode the escaped HTML entity
      const discountMessage = optionData.discountMessage
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');
      labelRight.textContent = discountMessage;
    } else {
      // If no discount message, clear the label
      labelRight.textContent = '';
    }
  }

  /**
   * Sets up the dialog trigger button to open the dialog.
   */
  #setupDialogTrigger() {
    const triggerButton = this.querySelector('[data-dialog-trigger]');
    if (!triggerButton) return;

    const dialogId = triggerButton.getAttribute('data-dialog-trigger');
    const dialogComponent = document.getElementById(dialogId);
    
    if (!dialogComponent) {
      console.warn('Dialog component not found:', dialogId);
      return;
    }

    triggerButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (dialogComponent.showDialog) {
        dialogComponent.showDialog();
      }
    });
  }
}

if (!customElements.get('subscription-options-component')) {
  customElements.define('subscription-options-component', SubscriptionOptionsComponent);
}
